import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { getWorkingProjectSessions } from '@/store/slices/project/selectors';
import { MediaItem } from '@/types/greenroom/media.types';
import { Shot } from '@/types/greenroom/shots.types';
import Container from '@ui/container/container';
import GreenroomButton from '@ui/greenroom-button/greenroom-button';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
import { GREENROOM_PLACEHOLDER_SHOTS } from './placeholder-shots';
import ProjectColorText from '@ui/text/text/project-color-text';

type ShotsHeaderProps = {
  loading: boolean;
  media: (MediaItem & { duration?: number })[] | null;
  shots: Shot[] | null;
  useDefaults: boolean;
  sessionUuid?: string;
};

const TextComponent = () => {
  return (
    <ProjectColorText contrastAgainstAppBackground className="L2">
      Edit Greenroom®
    </ProjectColorText>
  );
};

const ShotsHeader = ({ loading, media, shots, sessionUuid: _sessionUuid, useDefaults }: ShotsHeaderProps) => {
  const params = useParams<{ sessionUuid: string }>();

  const sessions = useAppSelector(getWorkingProjectSessions);

  const sessionUuid = _sessionUuid || params?.sessionUuid;

  const session = sessions?.find((s) => s.uuid === sessionUuid);

  const renderTotalShotDuration = useCallback(() => {
    if (!media && !useDefaults) return null;

    let totalSeconds = 0;
    if (useDefaults) {
      totalSeconds = GREENROOM_PLACEHOLDER_SHOTS.reduce((acc, shot) => {
        return acc + shot.duration_seconds;
      }, 0);
    } else if (!media || !media.length) {
      return null;
    } else {
      totalSeconds = media.reduce((acc, mediaItem) => {
        return acc + (mediaItem.duration || 0);
      }, 0);
    }

    // convert total seconds to hours, minutes, seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const hrs = hours ? `${hours}h ` : '';
    const min = minutes ? `${minutes}m ` : '';
    const sec = seconds ? `${seconds}s` : '';

    return `${hrs}${min}${sec}`;
  }, [media, useDefaults]);

  const renderShotsCount = useCallback(() => {
    if (useDefaults) {
      return `${GREENROOM_PLACEHOLDER_SHOTS.length} shots`;
    }

    if (!shots) return null;

    return `${shots.length} Shots`;
  }, [shots, useDefaults]);

  if (!session) return null;
  return (
    <div className="flex fd-row jc-space-between ai-center margin-b-8" data-component="ShotsHeader">
      <div className="flex fd-row jc-flex-start ai-center">
        <div className="margin-r-8">
          <Text tag="h5" className="H5" design="GlobalIcoText1">
            Showboard
          </Text>
        </div>
        <Container
          className="flex fd-row jc-flex-start ai-center"
          minWidth="50px"
          skeletonLoader={loading || ((!shots || !media) && !useDefaults)}
          skeletonLoaderBackground={{ start: 'rgba(var(--gray-1100-rgb), 0.5)', end: 'transparent' }}
        >
          <div>
            <Text color="var(--gray-500)" tag="span" className="S2">
              {renderShotsCount()}
            </Text>
          </div>
          <OptionalComponent display={useDefaults || (!!media && !!shots)}>
            <div className="margin-h-4">
              <Text tag="span" color="var(--gray-500)" className="S2">
                •
              </Text>
            </div>
          </OptionalComponent>
          <div>
            <Text color="var(--gray-500)" tag="span" className="S2">
              {renderTotalShotDuration()}
            </Text>
          </div>
        </Container>
      </div>

      <div>
        <GreenroomButton
          session={session}
          className="clear-button"
          TextComponent={<TextComponent />}
          backgroundColor="transparent"
          hoverBackgroundColor="transparent"
          hoverOpacity={0.8}
        />
      </div>
    </div>
  );
};

export default ShotsHeader;
